import React, { ReactNode, useMemo } from "react";
import { useQuery } from "react-query";
import { Theme, ThemeProvider } from "@mui/material";
import { fetchAppInstance } from "services/appService";
import { createTheme } from "@mui/material/styles";
import palette from "theme/palette";
import { AdminPaths, BasePaths } from 'routes/paths';
import { Roles, Modules } from "../modules/Admin/constants/personnel";
import { ReactComponent as LedgerIcon } from "assets/svgs/budget.svg";
import { ReactComponent as PaymentAdviceIcon } from "assets/svgs/payment-advice.svg";
import { ReactComponent as PaymentLedgerIcon } from "assets/svgs/payment-ledger.svg";
import { ReactComponent as FilesIcon } from "assets/svgs/files.svg";
import { ReactComponent as Workflow } from "assets/svgs/workflow-icon.svg";
import { ReactComponent as ExpenditureWhite } from "assets/svgs/expenditure-white.svg";
import { ReactComponent as ExpenditureClearanceIcon } from "assets/svgs/expenditureClearance.svg";
import { ReactComponent as GLWhite } from "assets/svgs/gl-white.svg";
import { ReactComponent as PersonnelsWhite } from "assets/svgs/personnels-white.svg";
import { ReactComponent as SettingsIcon } from "assets/svgs/settings.svg";
import { ReactComponent as WarrantWhite } from "assets/svgs/warrantIcon.svg";
import { ReactComponent as DashboardWhite } from "assets/svgs/dashboard-white.svg";
import { ReactComponent as ActivityIcon } from "assets/svgs/activity.svg";
import { ReactComponent as MDAWhite } from "assets/svgs/mda-white.svg";
import { ReactComponent as WorkbenchWhite } from "assets/svgs/workbench-white.svg";
import { capitalizeFirst } from "Helper";
import { SUPER_ADMIN_NAV_ITEMS } from "constants/sidebarItems";
import { IOption } from "adminModule/interfaces/personnelInterfact";
import {
  LazyAdminRouter,
  LazyAuthRouter,
  LazyMainLayout,
  LazyNotFound,
  LazySuperAdminRouter,
} from "routes/lazyRouters";

interface IAppInstanceProps {
  children: ReactNode;
  theme: Theme;
}

function getFaviconEl() {
  return document.getElementById("favicon") as HTMLAnchorElement | null;
}

function getAppleTouchIcon() {
  return document.getElementById("appleTouchIcon") as HTMLAnchorElement | null;
}

function getappTitle() {
  return document.getElementById("appTitle");
}

const AppInstanceContext = React.createContext<{}>({});

const AppInstanceProvider = ({ children, theme }: IAppInstanceProps) => {
  const domain = window.location.hostname;
  const instanceSlug = process.env.REACT_APP_INSTANCE_DOMAIN
    ? process.env.REACT_APP_INSTANCE_DOMAIN
    : domain;

  const { data, isError } = useQuery(
    ["appInstance", { instanceSlug }],
    fetchAppInstance,
    {
      enabled: !!instanceSlug,
      onSuccess: (data) => {
        console.log("Here is the data", data);
        const favicon: HTMLAnchorElement | null = getFaviconEl(); // Accessing favicon element
        const appTouch: HTMLAnchorElement | null = getAppleTouchIcon();
        const appTitle: HTMLElement | null = getappTitle();
        if (favicon) {
          favicon.href = data?.data?.logo;
        }
        if (appTouch) {
          appTouch.href = data?.data?.logo;
        }
        if (appTitle) {
          appTitle.innerHTML = `${data?.data?.name} EMS`;
        }
      },
    }
  );

  const instanceTheme = React.useMemo(() => {
    if (data?.data?.color_variants) {
      return createTheme({
        ...theme,
        palette: {
          primary: {
            main:
              (data?.data?.color_variants && data?.data?.color_variants?.color_2) ||
              palette.primary.main,
            light: data?.data?.color_variants?.color_8 || palette.primary.light,
            dark: data?.data?.color_variants?.color_6 || palette.primary.shade,
          },
          secondary: {
            main:
              (data?.data?.color_variants && data?.data?.color_variants?.color_2) ||
              palette.secondary.main,
            light: data?.data?.color_variants?.color_8 || palette.secondary.light,
            dark: data?.data?.color_variants?.color_6 || palette.secondary.main,
          },
          // @ts-ignore
          tertiary: {
            main:
              (data?.data?.color_variants && data?.data?.color_variants?.color_1) ||
              palette.tertiary.main,
          },
          // @ts-ignore
          background: {
            ...theme?.palette?.background,
            // @ts-ignore
            avatar: data?.data?.color_variants?.color_2,
          },
        },
        components: {
          ...theme.components,
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: 4,
                textTransform: "capitalize",
                color: data?.data?.color_variants?.color_8,
                "&:hover": {
                  // border: "none",
                  background: data?.data?.color_variants?.color_1,
                  color: data?.data?.color_variants?.color_8,
                },
              },
              outlinedPrimary: {
                border: "0px",
                color: data?.data?.color_variants?.color_2,
                background: data?.data?.color_variants?.color_8,
                "&:hover": {
                  border: "none",
                  background: data?.data?.color_variants?.color_7,
                  color: data?.data?.color_variants?.color_2,
                },
              },
              outlinedSecondary: {
                border: "0px",
                color: (data?.data?.color_variants && data?.data?.color_variants?.color_2) || "",
                background: data?.data?.color_variants?.color_8,
                // lighten(data?.data?.secondary_color, 0.9),
                "&:hover": {
                  border: "none",
                  background: data?.data?.color_variants?.color_1,
                  // lighten(data?.data?.secondary_color, 0.7),
                },
              },
            },
          },
          // MuiTextField: {
          //   styleOverrides: {
          //     root: {
          //       "& label": {
          //         // opacity: ".5",
          //         color: "#ccc",
          //       },
          //       // Default state of text input field font.
          //       "& .MuiInputBase-input.MuiInput-input": {
          //         fontFamily: "Manrope",
          //         color: "#2A2B2F",
          //       },

          //       // Default state of underline.
          //       "& .MuiInputBase-root.MuiInput-root.MuiInput-underline.MuiInputBase-formControl::before":
          //         {
          //           borderBottomColor: 'green',
          //         },

          //       // On hover state of underline.
          //       "& .MuiInputBase-root.MuiInput-root.MuiInput-underline.MuiInputBase-formControl:hover::before":
          //         {
          //           borderBottomColor: "blue",
          //         },

          //       // On focus state of underline.
          //       "& .MuiInputBase-root.MuiInput-root.MuiInput-underline.MuiInputBase-formControl.Mui-focused::after":
          //         {
          //           borderBottom: "2px solid #2E98EF",
          //         },
          //       // Default state of label.
          //       "& .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-formControl.MuiInputLabel-animated.MuiInputLabel-standard.MuiFormLabel-colorPrimary.MuiInputLabel-root.MuiInputLabel-formControl.MuiInputLabel-animated.MuiInputLabel-standard":
          //         {
          //           color: "green",
          //         },

          //       // On focus state of label (after text is entered into the form field).
          //       "& .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-formControl.MuiInputLabel-animated.MuiInputLabel-shrink.MuiInputLabel-standard.MuiFormLabel-colorPrimary.MuiFormLabel-filled.MuiInputLabel-root.MuiInputLabel-formControl.MuiInputLabel-animated.MuiInputLabel-shrink.MuiInputLabel-standard":
          //         {
          //           color: "yellow",
          //         },
          //     },
          //   },
          // },
        },
      });
    }
    return theme;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.data]);

  const contextValue = useMemo(
    () => ({
      ...(data?.data || {}),
      slug: instanceSlug,
    }),
    [data?.data, instanceSlug]
  );

  if (isError) return <p>Error: Failed to get instance slug. </p>;

  return (
    <AppInstanceContext.Provider value={contextValue}>
      <ThemeProvider theme={instanceTheme}>{children}</ThemeProvider>
    </AppInstanceContext.Provider>
  );
};

export const useAppInstance: any = () => {
  return React.useContext(AppInstanceContext);
};

export const useAppInstanceConst = () => {
  const instanceData: any = React.useContext(AppInstanceContext);
  const getDashboardUrl = (user: any) => {
    if (!user?.roles) return `${AdminPaths.ADMIN_DASHBOARD}?mda=${user?.mda}`;
    if (user.roles.includes(Roles.ADMIN) || user.roles.includes(Roles.IT_ADMIN)) {
      return AdminPaths.ADMIN_DASHBOARD;
    }
    return `${AdminPaths.ADMIN_DASHBOARD}?mda=${user?.mda?.id} `;
  };

  const ADMIN_NAV_ITEMS = [
    {
      name: "Dashboard",
      url: getDashboardUrl,
      icon: (active: boolean) => <DashboardWhite />,
      roles: [Roles.ADMIN, Roles.IT_ADMIN, Roles.MDA_ADMIN],
      modules: [Modules.DMM, Modules.WMM, Modules.EMM],
      bottom: false,
    },
    {
      name: "Dashboard",
      url: () => AdminPaths.GENERAL_DASHBOARD,
      icon: (active: boolean) => <DashboardWhite />,
      roles: [
        Roles.SE_FUND_RELEASE,
        Roles.SE_EXPENDITURE,
        Roles.FUNDS_MANAGER,
        Roles.SE_WARRANT,
        Roles.MDA_ACCOUNTING_OFFICER,
        Roles.MDA_SUPERVISING_OFFICER,
        Roles.MDA_AUTHORISER,
        Roles.MDA_AUTHORIZER,
        Roles.MDA_CHECKER,
        Roles.TREASURY_CASH_OFFICER,
      ],
      bottom: false,
      modules: [Modules.WMM, Modules.EMM],
    },
    {
      name: "Activities",
      url: () => AdminPaths.ACTIVITIES,
      icon: (active: boolean) => <ActivityIcon />,
      roles: [Roles.ADMIN, Roles.IT_ADMIN],
      modules: [Modules.DMM, Modules.WMM, Modules.EMM],
      bottom: false,
    },

    {
      name: "Workbench",
      url: () => AdminPaths.WORKBENCH,
      icon: (active: boolean) => <WorkbenchWhite />,
      roles: Object.values(Roles)
        .filter((v: string) => v !== Roles.ADMIN)
        .filter((v: string) => v !== Roles.IT_ADMIN)
        .filter((v: string) => v !== Roles.MDA_ADMIN)
        .filter((v: string) => v !== Roles.SUPERADMIN)
        .filter((v: string) => v !== Roles.GLOBAL_AUDITOR)
        .filter((v: string) => v !== Roles.SE_EXPENDITURE)
        .filter((v: string) => v !== Roles.SE_FUND_RELEASE)
        .filter((v: string) => v !== Roles.SE_WARRANT)
        .filter((v: string) => v !== Roles.EMM_ADMIN),
      modules: [Modules.WMM, Modules.EMM, Modules.DMM],
      bottom: false,
    },
    ...(instanceData?.support_running_grant
      ? [
          {
            name: "Bulk Running Grant",
            url: () => AdminPaths.BULK_RUNNING_GRANT,
            icon: (active: boolean) => <WarrantWhite />,
            roles: [Roles.WARRANT_INITIATOR],
            modules: [Modules.WMM, Modules.EMM],
            bottom: false,
          },
        ]
      : []),
    {
      name: "Requests",
      url: () => AdminPaths.REQUESTS,
      icon: (active: boolean) => <ExpenditureWhite />,
      roles: Object.values(Roles)
        .filter((v: string) => v !== Roles.SUPERADMIN)
        .filter((v: string) => v !== Roles.BUDGET_ADMIN)
        .filter((v: string) => v !== Roles.GLO_INITIATOR)
        .filter((v: string) => v !== Roles.GLO_REVIEWER)
        .filter((v: string) => v !== Roles.GLOBAL_AUDITOR)
        .filter((v: string) => v !== Roles.EMM_ADMIN)
        .filter((v: string) => v !== Roles.RECONCILIATION_INITIATOR)
        .filter((v: string) => v !== Roles.RECONCILIATION_AUTHORISER),
      modules: [Modules.GMM, Modules.EMM, Modules.DMM],

      bottom: false,
    },
    {
      name: "Files",
      url: () => AdminPaths.FILES,
      roles: [
        Roles.ADMIN,
        Roles.IT_ADMIN,
        // Roles.EXPENDITURE_REQUEST_REVIEWER,
        Roles.EXPENDITURE_CLEARANCE_INITIATOR,
        Roles.BUDGET_ADMIN,
        Roles.MDA_ADMIN,
        Roles.MDA_INTERNAL_AUDITOR,
        Roles.MDA_SUPERVISING_OFFICER,
        Roles.MDA_ACCOUNTING_OFFICER,
        Roles.MDA_AUTHORISER,
        Roles.MDA_AUTHORIZER,
        Roles.MDA_CHECKER,
        Roles.MDA_INITIATOR,
        Roles.MDA_REVIEWER,
        Roles.MDA_MODERATOR,
      ],
      modules: [Modules.DMM, Modules.WMM],
      icon: (active: boolean) => <FilesIcon />,
      bottom: false,
    },
    {
      name: "MDA",
      url: () => AdminPaths.MDAS,
      icon: (active: boolean) => <MDAWhite />,
      roles: [Roles.ADMIN, Roles.IT_ADMIN],
      bottom: false,
      modules: [Modules.DMM, Modules.WMM, Modules.EMM],
    },
    {
      name: "MDA",
      url: (user: any) => `${AdminPaths.MDA_DETAILS}/${user?.mda?.id}`,
      icon: (active: boolean) => <MDAWhite />,
      roles: [Roles.MDA_ADMIN, Roles.IT_ADMIN],
      bottom: false,
      modules: [Modules.DMM, Modules.GMM, Modules.EMM, Modules.EMM],
    },
    {
      name: "Personnels",
      url: () => AdminPaths.PERSONNELS,
      icon: (active: boolean) => <PersonnelsWhite />,
      roles: [Roles.ADMIN, Roles.IT_ADMIN],
      modules: [Modules.GMM, Modules.EMM, Modules.DMM, Modules.GMM],

      bottom: false,
    },
    {
      name: "Workflows",
      url: () => AdminPaths.WORKFLOW,
      icon: (active: boolean) => <Workflow height={20} width={20} />,
      roles: [
        Roles.ADMIN,
        Roles.IT_ADMIN,
        Roles.MDA_ADMIN,
        Roles.MDA_AUTHORISER,
        Roles.MDA_CHECKER,
        Roles.MDA_INTERNAL_AUDITOR,
        Roles.MDA_REVIEWER,
        Roles.MDA_SUPERVISING_OFFICER,
        Roles.MDA_ACCOUNTING_OFFICER,
        Roles.SE_WARRANT,
        Roles.SE_FUND_RELEASE,
        Roles.SE_EXPENDITURE,
        Roles.MDA_INITIATOR,
        Roles.EXPENDITURE_CLEARANCE_AUTHORISER,
        Roles.EXPENDITURE_CLEARANCE_INITIATOR,
        Roles.EXPENDITURE_REQUEST_REVIEWER,
        Roles.WARRANT_REQUEST_REVIEWER,
        Roles.WARRANT_REQUEST_AUTHORISER,
        Roles.WARRANT_AUTHORISER,
        Roles.WARRANT_INITIATOR,
        Roles.DIRECTOR_OF_EXPENDITURE,
        Roles.WARRANT_REQUEST_APPROVER,
        Roles.EXPENDITURE_CLEARANCE_CHECKER,
        Roles.EXPENDITURE_CLEARANCE_ENDORSER,
        Roles.EXPENDITURE_DIGITIZATION_INITIATOR,
        Roles.EXPENDITURE_DIGITIZATION_REVIEWER,
        Roles.EXPENDITURE_DIGITIZATION_AUTHORISER,
        Roles.WARRANT_REQUEST_CHECKER,
        Roles.WARRANT_REQUEST_APPROVER,
        Roles.WARRANT_DIGITIZATION_INITIATOR,
        Roles.WARRANT_GENERATION_AUTHORISER,
        Roles.WARRANT_DIGITIZATION_REVIEWER,
        Roles.WARRANT_DIGITIZATION_AUTHORISER,
        Roles.WARRANT_GENERATION_INITIATOR,
        Roles.WARRANT_GENERATION_AUTHORISER,
        Roles.WARRANT_GENERATION_REVIEWER,
      ],
      modules: [Modules.DMM, Modules.WMM, Modules.EMM],
      bottom: false,
    },
    {
      name: `${capitalizeFirst(instanceData?.warrant_name)}s`,
      url: () => AdminPaths.WARRANTS,
      icon: (active: boolean) => <WarrantWhite />,
      roles: Object.values(Roles)
        .filter((v: string) => v !== Roles.SUPERADMIN)
        .filter((v: string) => v !== Roles.BUDGET_ADMIN)
        .filter((v: string) => v !== Roles.EMM_ADMIN)
        .filter((v: string) => v !== Roles.CASH_OFFICER)
        .filter((v: string) => v !== Roles.GLO_INITIATOR)
        .filter((v: string) => v !== Roles.GLO_REVIEWER)
        .filter((v: string) => v !== Roles.FUNDS_MANAGER)
        .filter((v: string) => v !== Roles.SE_FUND_RELEASE)
        .filter((v: string) => v !== Roles.GLOBAL_AUDITOR)
        .filter((v: string) => v !== Roles.EXPENDITURE_CLEARANCE_INITIATOR)
        .filter((v: string) => v !== Roles.EXPENDITURE_CLEARANCE_AUTHORISER)
        .filter((v: string) => v !== Roles.EXPENDITURE_REQUEST_REVIEWER)
        .filter((v: string) => v !== Roles.PAYMENT_REQUEST_PROCESSING_INITIATOR)
        .filter((v: string) => v !== Roles.PAYMENT_REQUEST_PROCESSING_REVIEWER)
        .filter((v: string) => v !== Roles.PAYMENT_REQUEST_PROCESSING_AUTHORISER)
        .filter((v: string) => v !== Roles.CONTRACT_PAYMENT_REQUEST_PROCESSING_CHECKER)
        .filter((v: string) => v !== Roles.TREASURY_CASH_OFFICER)
        .filter((v: string) => v !== Roles.MDA_INTERNAL_AUDITOR)
        .filter((v: string) => v !== Roles.EXPENDITURE_CLEARANCE_CHECKER)
        .filter((v: string) => v !== Roles.EXPENDITURE_CLEARANCE_ENDORSER)
        .filter((v: string) => v !== Roles.EXPENDITURE_CLEARANCE_AUTHORISER)
        .filter((v: string) => v !== Roles.EXPENDITURE_CLEARANCE_APPROVER)
        .filter((v: string) => v !== Roles.EXPENDITURE_DIGITIZATION_INITIATOR)
        .filter((v: string) => v !== Roles.EXPENDITURE_DIGITIZATION_REVIEWER)
        .filter((v: string) => v !== Roles.EXPENDITURE_DIGITIZATION_AUTHORISER)
        .filter((v: string) => v !== Roles.RECONCILIATION_INITIATOR)
        .filter((v: string) => v !== Roles.RECONCILIATION_AUTHORISER),

      modules: [Modules.EMM],

      bottom: false,
    },

    ...(!instanceData?.supports_expenditure
      ? []
      : [
          {
            name: "Expenditure Clearance",
            url: () => AdminPaths.EXPENDITURE_CLEARANCE,
            icon: (active: boolean) => <ExpenditureClearanceIcon />,
            roles: Object.values(Roles)
              .filter((v: string) => v !== Roles.SUPERADMIN)
              .filter((v: string) => v !== Roles.BUDGET_ADMIN)
              .filter((v: string) => v !== Roles.GLOBAL_AUDITOR)
              .filter((v: string) => v !== Roles.GLO_INITIATOR)
              .filter((v: string) => v !== Roles.GLO_REVIEWER)
              .filter((v: string) => v !== Roles.FUNDS_MANAGER)
              .filter((v: string) => v !== Roles.SE_FUND_RELEASE)
              .filter((v: string) => v !== Roles.EMM_ADMIN)
              .filter((v: string) => v !== Roles.MDA_INTERNAL_AUDITOR)
              .filter((v: string) => v !== Roles.WARRANT_AUTHORISER)
              .filter((v: string) => v !== Roles.WARRANT_INITIATOR)
              .filter((v: string) => v !== Roles.WARRANT_REQUEST_APPROVER)
              .filter((v: string) => v !== Roles.WARRANT_REQUEST_REVIEWER)
              .filter((v: string) => v !== Roles.WARRANT_REQUEST_APPROVER)
              .filter((v: string) => v !== Roles.CASH_OFFICER)
              .filter((v: string) => v !== Roles.WARRANT_REQUEST_CHECKER)
              .filter((v: string) => v !== Roles.WARRANT_REQUEST_AUTHORISER)
              .filter((v: string) => v !== Roles.WARRANT_REQUEST_APPROVER)
              .filter((v: string) => v !== Roles.WARRANT_DIGITIZATION_INITIATOR)
              .filter((v: string) => v !== Roles.WARRANT_DIGITIZATION_REVIEWER)
              .filter((v: string) => v !== Roles.WARRANT_DIGITIZATION_AUTHORISER)
              .filter((v: string) => v !== Roles.WARRANT_REQUEST_ENDORSER)
              .filter((v: string) => v !== Roles.WARRANT_GENERATION_INITIATOR)
              .filter((v: string) => v !== Roles.WARRANT_GENERATION_AUTHORISER)
              .filter((v: string) => v !== Roles.WARRANT_GENERATION_REVIEWER)
              .filter((v: string) => v !== Roles.PAYMENT_REQUEST_PROCESSING_REVIEWER)
              .filter((v: string) => v !== Roles.PAYMENT_REQUEST_PROCESSING_AUTHORISER)
              .filter((v: string) => v !== Roles.RECONCILIATION_INITIATOR)
              .filter((v: string) => v !== Roles.RECONCILIATION_AUTHORISER)
              .filter((v: string) => v !== Roles.TREASURY_CASH_OFFICER),
            modules: [Modules.EMM],

            bottom: false,
          },
        ]),
    {
      name: "Payments",
      url: () => AdminPaths.PAYMENTS,
      icon: (active: boolean) => <MDAWhite />,
      roles: Object.values(Roles)
        .filter((v: string) => v !== Roles.SUPERADMIN)
        .filter((v: string) => v !== Roles.BUDGET_ADMIN)
        .filter((v: string) => v !== Roles.GLOBAL_AUDITOR)
        .filter((v: string) => v !== Roles.EMM_ADMIN)
        .filter((v: string) => v !== Roles.SE_EXPENDITURE)
        .filter((v: string) => v !== Roles.GLO_INITIATOR)
        .filter((v: string) => v !== Roles.VOTE_BOOK_OFFICER)
        .filter((v: string) => v !== Roles.DIRECTOR_OF_EXPENDITURE)
        .filter((v: string) => v !== Roles.CONTRACT_PAYMENT_REQUEST_REVIEWER)
        .filter((v: string) => v !== Roles.EXPENDITURE_CLEARANCE_CHECKER)
        .filter((v: string) => v !== Roles.EXPENDITURE_CLEARANCE_ENDORSER)
        .filter((v: string) => v !== Roles.EXPENDITURE_CLEARANCE_AUTHORISER)
        .filter((v: string) => v !== Roles.EXPENDITURE_CLEARANCE_APPROVER)
        .filter((v: string) => v !== Roles.EXPENDITURE_DIGITIZATION_INITIATOR)
        .filter((v: string) => v !== Roles.EXPENDITURE_DIGITIZATION_AUTHORISER)
        .filter((v: string) => v !== Roles.EXPENDITURE_DIGITIZATION_REVIEWER)
        .filter((v: string) => v !== Roles.WARRANT_REQUEST_CHECKER)
        .filter((v: string) => v !== Roles.WARRANT_REQUEST_REVIEWER)
        .filter((v: string) => v !== Roles.WARRANT_REQUEST_APPROVER)
        .filter((v: string) => v !== Roles.WARRANT_DIGITIZATION_INITIATOR)
        .filter((v: string) => v !== Roles.WARRANT_DIGITIZATION_REVIEWER)
        .filter((v: string) => v !== Roles.WARRANT_DIGITIZATION_AUTHORISER)
        .filter((v: string) => v !== Roles.EXPENDITURE_REQUEST_REVIEWER)
        .filter((v: string) => v !== Roles.WARRANT_REQUEST_ENDORSER)
        .filter((v: string) => v !== Roles.WARRANT_REQUEST_AUTHORISER)
        .filter((v: string) => v !== Roles.WARRANT_GENERATION_INITIATOR)
        .filter((v: string) => v !== Roles.WARRANT_GENERATION_AUTHORISER)
        .filter((v: string) => v !== Roles.WARRANT_GENERATION_REVIEWER)
        .filter((v: string) => v !== Roles.CONTRACT_PAYMENT_REQUEST_PROCESSING_CHECKER),
      modules: [Modules.EMM],
    },
    {
      name: "Vote Book",
      url: () => AdminPaths.VOTE_BOOK,
      icon: (active: boolean) => <WorkbenchWhite />,
      roles: [
        Roles.ADMIN,
        Roles.MDA_ADMIN,
        Roles.MDA_AUTHORISER,
        Roles.MDA_INTERNAL_AUDITOR,
        Roles.MDA_SUPERVISING_OFFICER,
        Roles.MDA_ACCOUNTING_OFFICER,
        Roles.SE_WARRANT,
        Roles.SE_FUND_RELEASE,
        Roles.SE_EXPENDITURE,
        Roles.MDA_INITIATOR,
        Roles.EXPENDITURE_CLEARANCE_AUTHORISER,
        Roles.EXPENDITURE_CLEARANCE_INITIATOR,
        Roles.EXPENDITURE_REQUEST_REVIEWER,
        Roles.WARRANT_REQUEST_REVIEWER,
        Roles.WARRANT_REQUEST_AUTHORISER,
        Roles.WARRANT_AUTHORISER,
        Roles.WARRANT_INITIATOR,
        Roles.DIRECTOR_OF_EXPENDITURE,
        Roles.WARRANT_REQUEST_APPROVER,
        Roles.EXPENDITURE_CLEARANCE_CHECKER,
        Roles.EXPENDITURE_CLEARANCE_ENDORSER,
        Roles.EXPENDITURE_DIGITIZATION_INITIATOR,
        Roles.EXPENDITURE_DIGITIZATION_REVIEWER,
        Roles.EXPENDITURE_DIGITIZATION_AUTHORISER,
        Roles.WARRANT_REQUEST_CHECKER,
        Roles.WARRANT_REQUEST_APPROVER,
        Roles.WARRANT_DIGITIZATION_INITIATOR,
        Roles.WARRANT_GENERATION_AUTHORISER,
        Roles.WARRANT_DIGITIZATION_REVIEWER,
        Roles.WARRANT_DIGITIZATION_AUTHORISER,
        Roles.WARRANT_GENERATION_INITIATOR,
        Roles.WARRANT_GENERATION_AUTHORISER,
        Roles.WARRANT_GENERATION_REVIEWER,
        Roles.TREASURY_CASH_OFFICER,
        Roles.PAYMENT_REQUEST_PROCESSING_INITIATOR,
        Roles.PAYMENT_REQUEST_PROCESSING_AUTHORISER,
        Roles.PAYMENT_REQUEST_PROCESSING_REVIEWER,
      ],
      modules: [Modules.EMM],

      bottom: false,
    },

    {
      name: "Budget",
      url: () => AdminPaths.BUDGETS,
      icon: (active: boolean) => <LedgerIcon />,
      roles: [Roles.ADMIN, Roles.IT_ADMIN, Roles.BUDGET_ADMIN],
      modules: [Modules.EMM],
    },

    {
      name: "General Ledger",
      url: () => AdminPaths.LEDGER,
      icon: (active: boolean) => <GLWhite />,
      roles: [
        Roles.GLO_INITIATOR,
        Roles.GLO_REVIEWER,
        Roles.ADMIN,
        Roles.IT_ADMIN,
        Roles.FUNDS_MANAGER,
        Roles.SE_EXPENDITURE,
        Roles.SE_FUND_RELEASE,
        Roles.RECONCILIATION_AUTHORISER,
        Roles.RECONCILIATION_INITIATOR,
      ],
      modules: [Modules.EMM],
    },
    {
      name: "Contracts",
      url: () => AdminPaths.CONTRACTS_LEDGER,
      icon: (active: boolean) => <GLWhite />,
      roles: [
        Roles.ADMIN,
        Roles.IT_ADMIN,
        Roles.SE_FUND_RELEASE,
        Roles.SE_EXPENDITURE,
        Roles.SE_WARRANT,
        Roles.PAYMENT_REQUEST_PROCESSING_REVIEWER,
        Roles.PAYMENT_REQUEST_PROCESSING_AUTHORISER,
        Roles.PAYMENT_REQUEST_PROCESSING_INITIATOR,
        Roles.CONTRACT_PAYMENT_REQUEST_PROCESSING_CHECKER,
        Roles.MDA_ACCOUNTING_OFFICER,
        Roles.MDA_SUPERVISING_OFFICER,
        Roles.FUNDS_MANAGER,
        Roles.DIRECTOR_OF_EXPENDITURE,
        Roles.MDA_ADMIN,
      ],
      modules: [Modules.EMM],
    },
    {
      name: "Payment Advice",
      url: () => AdminPaths.PAYMENT_ADVICE,
      icon: (active: boolean) => <PaymentAdviceIcon />,
      roles: [Roles.CASH_OFFICER, Roles.FUNDS_MANAGER, Roles.ADMIN, Roles.IT_ADMIN],
      modules: [Modules.EMM],
    },
    {
      name: "Payment Ledger",
      url: () => AdminPaths.PAYMENT_LEDGER,
      icon: (active: boolean) => <PaymentLedgerIcon />,
      roles: [
        Roles.CASH_OFFICER,
        Roles.FUNDS_MANAGER,
        Roles.ADMIN,
        Roles.IT_ADMIN,
        Roles.SE_EXPENDITURE,
        Roles.SE_FUND_RELEASE,
      ],
      modules: [Modules.EMM],
    },
    {
      name: "Reconciliations",
      url: () => AdminPaths.RECONCILIATION,
      icon: (active: boolean) => <WorkbenchWhite />,
      roles: [
        Roles.RECONCILIATION_AUTHORISER,
        Roles.RECONCILIATION_INITIATOR,
        Roles.ADMIN,
        Roles.IT_ADMIN,
      ],
      modules: [Modules.EMM],
    },
    {
      name: "Settings",
      url: () => AdminPaths.SETTINGS,
      icon: (active: boolean, color?: string) => <SettingsIcon />,
      bottom: true,
      onClick: () => {},
      roles: [Roles.IT_ADMIN, Roles.ADMIN, Roles.SUPERADMIN, Roles.MDA_ADMIN],
      modules: [Modules.GMM, Modules.DMM, Modules.EMM, Modules.WMM],
    },
    {
      name: "Audit Log",
      url: () => AdminPaths.AUDITOR_WORKBENCH,
      icon: (active: boolean) => <WorkbenchWhite />,
      roles: [Roles.GLOBAL_AUDITOR, Roles.MDA_INTERNAL_AUDITOR],
      modules: [Modules.GMM, Modules.EMM],

      bottom: false,
    },

    {
      name: "Template",
      url: () => AdminPaths.TEMPLATE,
      icon: (active: boolean) => <MDAWhite />,
    },
  ];

  const BaseRoutes = [
    {
      path: "/*",
      exact: true,
      component: LazyAuthRouter,
      Layout: null,
      useAuth: false,
    },
    {
      path: `${BasePaths.ADMIN}/*`,
      exact: true,
      component: LazyAdminRouter,
      Layout: LazyMainLayout,
      useAuth: true,
      sidenavItems: ADMIN_NAV_ITEMS,
    },
    {
      path: `${BasePaths.SUPERADMIN}/*`,
      exact: true,
      component: LazySuperAdminRouter,
      Layout: LazyMainLayout,
      useAuth: true,
      sidenavItems: SUPER_ADMIN_NAV_ITEMS,
      isSuperAdmin: true,
    },
    {
      path: "*",
      exact: false,
      component: LazyNotFound,
      Layout: null,
      useAuth: false,
    },
  ];

  const ROLES_OPTIONS = [
    { label: "Super Admin", value: "SUPERADMIN" },
    { label: "Admin", value: "ADMIN" },
    { label: "IT Admin", value: "IT_ADMIN" },
    { label: "MDA Admin", value: "MDA_ADMIN" },
    { label: "MDA Accounting Officer", value: "MDA_ACCOUNTING_OFFICER" },
    { label: "MDA Reviewer", value: "MDA_REVIEWER" },
    { label: "MDA Initiator", value: "MDA_INITIATOR" },
    { label: "MDA Internal Auditor", value: "MDA_INTERNAL_AUDITOR" },
    { label: "MDA Authoriser", value: "MDA_AUTHORISER" },
    { label: "MDA Checker", value: "MDA_CHECKER" },
    { label: "MDA Supervising Officer", value: "MDA_SUPERVISING_OFFICER" },
    { label: "Budget Admin", value: "BUDGET_ADMIN" },
    { label: "EMM Admin", value: "EMM_ADMIN" },
    { label: "Global Auditor", value: "GLOBAL_AUDITOR" },
    { label: "GLO Initiator", value: "GLO_INITIATOR" },
    { label: "Expenditure Manager", value: "DIRECTOR_OF_EXPENDITURE" },
    { label: `${instanceData?.warrant_name} Initiator`, value: "WARRANT_INITIATOR" },
    { label: `${instanceData?.warrant_name} Authoriser`, value: "WARRANT_AUTHORISER" },
    { label: `${instanceData?.warrant_name} Request Reviewer`, value: "WARRANT_REQUEST_REVIEWER" },
    {
      label: `${instanceData?.warrant_name} Request Authoriser`,
      value: "WARRANT_REQUEST_AUTHORISER",
    },
    { label: `${instanceData?.warrant_name} Request Checker`, value: "WARRANT_REQUEST_CHECKER" },
    { label: `${instanceData?.warrant_name} Request Endorser`, value: "WARRANT_REQUEST_ENDORSER" },
    {
      label: `${instanceData?.warrant_name} Generation Reviewer`,
      value: "WARRANT_GENERATION_REVIEWER",
    },
    {
      label: `${instanceData?.warrant_name} Generation Authoriser`,
      value: "WARRANT_GENERATION_AUTHORISER",
    },
    {
      label: `${instanceData?.warrant_name} Generation Initiator`,
      value: "WARRANT_GENERATION_INITIATOR",
    },
    {
      label: "PR Processing Reviewer",
      value: "PAYMENT_REQUEST_PROCESSING_REVIEWER",
    },
    {
      label: "PR Processing Initiator",
      value: "PAYMENT_REQUEST_PROCESSING_INITIATOR",
    },
    {
      label: "PR Processing Authoriser",
      value: "PAYMENT_REQUEST_PROCESSING_AUTHORISER",
    },
    {
      label: "Expenditure Request Reviewer",
      value: "EXPENDITURE_REQUEST_REVIEWER",
    },
    {
      label: "Expenditure Clearance Initiator",
      value: "EXPENDITURE_CLEARANCE_INITIATOR",
    },
    {
      label: "Expenditure Clearance Authoriser",
      value: "EXPENDITURE_CLEARANCE_AUTHORISER",
    },
    { label: "Expenditure Clearance Endorser", value: "EXPENDITURE_CLEARANCE_ENDORSER" },
    { label: "Expenditure digitization Initiator", value: "EXPENDITURE_DIGITIZATION_INITIATOR" },
    { label: "Expenditure digitization Reviewer", value: "EXPENDITURE_DIGITIZATION_REVIEWER" },
    { label: "Expenditure digitization Authoriser", value: "EXPENDITURE_DIGITIZATION_AUTHORISER" },
    {
      label: `${instanceData?.warrant_name} digitization Initiator`,
      value: "WARRANT_DIGITIZATION_INITIATOR",
    },
    {
      label: `${instanceData?.warrant_name} digitization Reviewer`,
      value: "WARRANT_DIGITIZATION_REVIEWER",
    },
    {
      label: `${instanceData?.warrant_name} digitization Authoriser`,
      value: "WARRANT_DIGITIZATION_AUTHORISER",
    },
    { label: "Expenditure Clearance Checker", value: "EXPENDITURE_CLEARANCE_CHECKER" },
    { label: "Expenditure Clearance Approver", value: "EXPENDITURE_CLEARANCE_APPROVER" },
    {
      label: "CPR Processing Checker",
      value: "CONTRACT_PAYMENT_REQUEST_PROCESSING_CHECKER",
    },
    {
      label: "Treasury Cash Officer",
      value: "TREASURY_CASH_OFFICER",
    },
    {
      label: "Funds Manager",
      value: "FUNDS_MANAGER",
    },
    {
      label: "GLO Reviewer",
      value: "GLO_REVIEWER",
    },
    {
      label: "Cash Officer",
      value: "CASH_OFFICER",
    },
    {
      label: "State Executive Expenditure",
      value: "SE_EXPENDITURE",
    },
    {
      label: "State Executive Fund Release",
      value: "SE_FUND_RELEASE",
    },
    {
      label: `State Executive ${instanceData?.warrant_name}`,
      value: "SE_WARRANT",
    },
    {
      label: "Vote Book Officer",
      value: "VOTE_BOOK_OFFICER",
    },
    {
      label: `${instanceData?.warrant_name} Request Approver`,
      value: "WARRANT_REQUEST_APPROVER",
    },
    {
      label: "Reconciliation Initiator",
      value: "RECONCILIATION_INITIATOR",
    },
    {
      label: "Reconciliation Authoriser",
      value: "RECONCILIATION_AUTHORISER",
    },
  ];

  const GetFilteredRoles = (roles: string[]): IOption[] => {
    return ROLES_OPTIONS.filter((v: IOption) => roles?.includes(v.value));
  };

  return { ADMIN_NAV_ITEMS, ROLES_OPTIONS, BaseRoutes, GetFilteredRoles };
};

export default AppInstanceProvider;
